import { isEmpty } from '@newfront-insurance/core';
import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { AnimatePresence, motion } from 'framer-motion';
import { File, FolderOpen, X, Loader2, AlertCircle } from 'lucide-react';
import { useCallback, type Dispatch, type SetStateAction } from 'react';

import type { FileAttachment } from '../../hooks/use-file-selection';

function getAttachmentLoadingLabel(attachment: FileAttachment) {
  if (attachment.processingStage === 'preparing') {
    return 'Preparing';
  }

  if (attachment.processingStage === 'uploading' || attachment.isUploading) {
    return 'Uploading';
  }

  return undefined;
}

function getAttachmentDisplayLabel(attachment: FileAttachment) {
  if (attachment.kind === 'folder') {
    return attachment.folderMetadata?.folderName ?? attachment.folderName ?? attachment.file.name;
  }

  return attachment.file.name;
}

function AttachmentStatusIcon({ attachment }: { attachment: FileAttachment }) {
  if (attachment.uploadError) {
    return <AlertCircle aria-hidden="true" className="size-4 shrink-0 text-red-600" />;
  }

  if (attachment.isUploading) {
    return (
      <Loader2
        aria-label={getAttachmentLoadingLabel(attachment)}
        className="text-steel-400 size-4 shrink-0 animate-spin"
      />
    );
  }

  if (attachment.kind === 'folder') {
    return <FolderOpen aria-hidden="true" className="text-steel-400 size-4 shrink-0" />;
  }

  return <File aria-hidden="true" className="text-steel-400 size-4 shrink-0" />;
}

export function InputAttachmentsPreview({
  attachments,
  setAttachments,
}: {
  attachments: Array<FileAttachment>;
  setAttachments: Dispatch<SetStateAction<Array<FileAttachment>>>;
}) {
  const handleRemoveAttachment = useCallback(
    (tempId: string) => {
      const attachmentToRemove = attachments.find((a) => a.tempId === tempId);
      if (attachmentToRemove) {
        browserLogger.info('[Nova] Removing attachment', {
          fileName: attachmentToRemove.file.name,
          tempId,
          wasUploading: attachmentToRemove.isUploading,
          hadError: !!attachmentToRemove.uploadError,
          fileUuid: attachmentToRemove.fileUuid,
        });
      }
      setAttachments((prev) => prev.filter((attachment) => attachment.tempId !== tempId));
    },
    [attachments, setAttachments],
  );

  if (isEmpty(attachments)) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto overflow-y-hidden">
      <AnimatePresence>
        {attachments.map((attachment) => {
          const displayLabel = getAttachmentDisplayLabel(attachment);

          return (
            <motion.div
              key={attachment.tempId}
              title={attachment.uploadError}
              data-testid="attachment-preview-item"
              data-upload-state={attachment.uploadError ? 'error' : attachment.isUploading ? 'uploading' : 'ready'}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{
                duration: 0.2,
                ease: 'easeInOut',
              }}
              className={cn(
                'relative inline-flex h-8 max-w-[180px] shrink-0 items-center gap-1.5 rounded-[30px] border bg-white px-2.5 py-1.5 transition-colors',
                'border-steel-200 text-steel-500 hover:border-steel-400',
                {
                  'border-yellow-400': attachment.isUploading,
                  'border-red-400': attachment.uploadError,
                },
              )}
            >
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <AttachmentStatusIcon attachment={attachment} />
                <span className="truncate text-[12px] leading-4 font-normal">{displayLabel}</span>
              </div>
              <Button
                aria-label={`Remove ${displayLabel}`}
                variant="transparent"
                size="small"
                className={cn(
                  'text-steel-400 hover:text-steel-500 h-fit w-fit shrink-0 rounded-full border-none bg-transparent p-0.5',
                  'hover:bg-steel-100',
                )}
                onClick={() => handleRemoveAttachment(attachment.tempId)}
                type="button"
                disabled={attachment.isUploading}
              >
                <X aria-hidden="true" className="size-3" />
              </Button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
